import React, { Component } from 'react';
import { Button } from 'react-bootstrap';
import Select from '../../common/select';
import { AddService } from '../../partial/newAddService';
import { TemplateList } from './TemplateList';
import { VehicleSpecificPricing } from '../../partial/VehicleSpecificPricing';
import BootstrapTable from 'react-bootstrap-table-next';
import "react-bootstrap-table/css/react-bootstrap-table.css";

import ServicesAPI, {
  buildCategoryLookup,
  mergeServicesWithPricing,
  extractPriceMatrix,
  buildPriceMatrix,
  filterOverlayCandidates,
  createServiceCategory,
  getServiceItemTemplates
} from "../../../services/servicesApiV2";
import { getUnitsOfMeasure } from "../../../services/inventoryService";
import { HTTPService } from "../../../services/httpService";
import ProductItemTemplateModal from "../../partial/ProductItemTemplateModal";
import WorkflowStepTemplateModal from "../../partial/WorkflowStepTemplateModal";

const API = new HTTPService();

export default class Services extends Component {

  state = {
    // Categories
    categories: [],                    // Array of category objects (installer's)
    allCategories: [],                 // All global categories
    categoriesById: {},                // Lookup by category_id
    selectedCategoryId: null,
    selectedCategoryName: "Category",
    showAddCategory: false,
    newCategoryName: "",

    // Services (master services with merged pricing)
    services: [],                      // Master services with nested pricing (for selected category)
    allInstallerServices: [],          // All master services for this installer
    selectedServiceId: null,
    selectedServiceName: "Service",

    // Selected service details
    currentService: null,              // Full service object
    serviceName: "",
    coverageLevel: null,               // FRONT | REAR | FULL | null
    notes: "",
    dataCollectRequired: "Y",
    allowMultiple: false,
    taxable: true,
    retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
    wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
    durations: { XL: 0, L: 0, M: 0, S: 0 },
    expectedQuantities: { XL: 0, L: 0, M: 0, S: 0 },

    // Category configs and units (for expected quantity labels)
    categoryConfigs: {},           // { category_name: { default_unit_id, default_unit_code, ... } }
    unitsById: {},                 // { unit_id: { unit_name, unit_code } }

    // Overlays
    currentOverlays: [],               // Services this one overlays
    availableOverlays: [],             // Candidates for overlay selection

    // UI State
    errors: {},
    message: "",
    showModal: false,
    modalAction: "",
    isTemplateMode: false,
    isLoading: false,

    // Vehicle-Specific Pricing
    showVehiclePricingModal: false,
    trimOverrideCount: 0,
    vehicleOverrides: [],  // List of vehicles with custom pricing

    // Service Item Templates
    showProductItemTemplateModal: false,
    showWorkflowStepTemplateModal: false,
    productItemCount: 0,
    workflowStepCount: 0
  }

  async componentDidMount() {
    await this.loadInstallerData();
  }

  // Load installer's services and derive available categories
  loadInstallerData = async () => {
    try {
      this.setState({ isLoading: true });

      // Load all global categories, category configs, and units in parallel
      const [allCategories, categoryConfigsRaw, unitsRaw] = await Promise.all([
        ServicesAPI.getServiceCategories(),
        API.get('/serviceCategoryConfig').then(r => r.data).catch(() => []),
        getUnitsOfMeasure().catch(() => [])
      ]);
      const categoriesById = buildCategoryLookup(allCategories);

      // Build category config lookup by name
      const categoryConfigs = {};
      if (Array.isArray(categoryConfigsRaw)) {
        categoryConfigsRaw.forEach(config => {
          categoryConfigs[config.category_name] = config;
        });
      }

      // Build units lookup by ID
      const unitsById = {};
      if (Array.isArray(unitsRaw)) {
        unitsRaw.forEach(unit => {
          unitsById[unit.unit_id] = unit;
        });
      }

      // Load all master services for this installer
      const installerServices = await ServicesAPI.getMasterServicesByInstaller(this.props.typeId);

      // Extract unique category IDs from installer's services
      const installerCategoryIds = [...new Set(installerServices.map(s => s.category_id))];

      // Filter to only categories this installer has services in
      const installerCategories = allCategories.filter(cat =>
        installerCategoryIds.includes(cat.category_id)
      );

      this.setState({
        categories: installerCategories,
        allCategories,
        categoriesById,
        categoryConfigs,
        unitsById,
        allInstallerServices: installerServices,
        selectedCategoryId: null,
        selectedCategoryName: "Category",
        isLoading: false
      });
    } catch (error) {
      console.error("Error loading installer data:", error);
      this.setState({ isLoading: false });
      alert("Error loading data. Please refresh the page.");
    }
  };

  // Load services for selected category
  loadServices = async (categoryId, forceRefresh = false) => {
    try {
      this.setState({ isLoading: true });

      let masterServices;

      if (forceRefresh || this.state.allInstallerServices.length === 0) {
        // Fetch fresh data from API
        masterServices = await ServicesAPI.getMasterServicesByCategory(
          this.props.typeId,
          categoryId
        );
      } else {
        // Filter from cached data
        masterServices = this.state.allInstallerServices.filter(
          s => s.category_id === categoryId
        );
      }

      // Get all pricing for this installer
      const allPricing = await ServicesAPI.getInstallerServices(this.props.typeId);

      // Merge services with their pricing
      const servicesWithPricing = mergeServicesWithPricing(masterServices, allPricing);

      this.setState({
        services: servicesWithPricing,
        selectedServiceId: null,
        selectedServiceName: "Service",
        currentService: null,
        serviceName: "",
        coverageLevel: null,
        notes: "",
        dataCollectRequired: "Y",
        retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
        wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
        durations: { XL: 0, L: 0, M: 0, S: 0 },
        expectedQuantities: { XL: 0, L: 0, M: 0, S: 0 },
        currentOverlays: [],
        availableOverlays: [],
        isLoading: false
      });
    } catch (error) {
      console.error("Error loading services:", error);
      this.setState({ isLoading: false });
      alert("Error loading services. Please try again.");
    }
  };

  // Load overlays for a selected service
  loadOverlays = async (serviceId) => {
    try {
      const overrides = await ServicesAPI.getServiceOverrides(serviceId);
      return overrides;
    } catch (error) {
      console.error("Error loading overlays:", error);
      return [];
    }
  };

  // Handle category selection
  handleCategoryChange = async (event) => {
    const { value } = event.target;

    if (value === "Category") {
      this.setState({
        selectedCategoryId: null,
        selectedCategoryName: "Category",
        services: [],
        selectedServiceId: null,
        selectedServiceName: "Service"
      });
      return;
    }

    if (value === "Add New Category") {
      this.setState({
        showAddCategory: true,
        newCategoryName: "",
        selectedCategoryId: null,
        selectedCategoryName: "Category",
        services: [],
        selectedServiceId: null,
        selectedServiceName: "Service"
      });
      return;
    }

    // Find category by name to get ID — check allCategories so newly-selected global categories work
    const category = this.state.allCategories.find(c => c.category_name === value)
      || this.state.categories.find(c => c.category_name === value);
    if (category) {
      this.setState({
        selectedCategoryId: category.category_id,
        selectedCategoryName: value
      });
      await this.loadServices(category.category_id);
    }
  };

  handleNewCategoryChange = (e) => {
    this.setState({ newCategoryName: e.target.value });
  };

  handleCancelNewCategory = () => {
    this.setState({ showAddCategory: false, newCategoryName: "" });
  };

  handleCreateCategory = async () => {
    const name = this.state.newCategoryName.trim();
    if (!name) return;

    try {
      this.setState({ isLoading: true });

      // Get installer_category from the installer's company record
      const company = JSON.parse(localStorage.getItem("companyAPI") || "{}");
      const installerCategory = company.installer_category || "Paint";

      const result = await createServiceCategory({
        categoryName: name,
        installerCategory: installerCategory
      });

      // Refresh all categories and select the new one
      await this.loadInstallerData();

      // Re-fetch allCategories to include the new one
      const allCategories = await ServicesAPI.getServiceCategories();
      const categoriesById = buildCategoryLookup(allCategories);

      const newCat = allCategories.find(c => c.category_id === result.id)
        || allCategories.find(c => c.category_name === name);

      this.setState({
        allCategories,
        categoriesById,
        showAddCategory: false,
        newCategoryName: "",
        isLoading: false
      });

      if (newCat) {
        this.setState({
          selectedCategoryId: newCat.category_id,
          selectedCategoryName: newCat.category_name
        });
        await this.loadServices(newCat.category_id);
      }
    } catch (error) {
      console.error("Error creating category:", error);
      this.setState({ isLoading: false });
      alert("Error creating category: " + (error.response?.data?.message || error.message));
    }
  };

  // Handle service selection
  handleServiceChange = async (event) => {
    const { value } = event.target;

    if (value === "Service") {
      this.setState({
        selectedServiceId: null,
        selectedServiceName: "Service",
        currentService: null,
        serviceName: "",
        coverageLevel: null,
        notes: "",
        dataCollectRequired: "Y",
        allowMultiple: false,
        taxable: true,
        retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
        wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
        durations: { XL: 0, L: 0, M: 0, S: 0 },
        expectedQuantities: { XL: 0, L: 0, M: 0, S: 0 },
        currentOverlays: [],
        availableOverlays: [],
        trimOverrideCount: 0,
        vehicleOverrides: [],
        productItemCount: 0,
        workflowStepCount: 0
      });
      return;
    }

    // Find service by name
    const service = this.state.services.find(s => s.service_name === value);
    if (service) {
      // Extract pricing matrix from the pricing records
      const { retailPrices, wholesalePrices, durations, expectedQuantities } = extractPriceMatrix(service.pricing || []);

      // Load current overlays
      const currentOverlays = await this.loadOverlays(service.id);

      // Calculate available overlay candidates
      const availableOverlays = filterOverlayCandidates(this.state.services, service);

      // Load trim overrides (count and list)
      const { count: trimOverrideCount, vehicles: vehicleOverrides } = await this.loadTrimOverrides(service.id);

      // Load service item template counts
      const { productItemCount, workflowStepCount } = await this.loadItemTemplateCounts(service.id);

      this.setState({
        selectedServiceId: service.id,
        selectedServiceName: value,
        currentService: service,
        serviceName: service.service_name,
        coverageLevel: service.coverage_level,
        notes: service.notes || "",
        dataCollectRequired: service.data_collect_required || "Y",
        allowMultiple: service.allow_multiple === 1 || service.allow_multiple === true,
        taxable: service.taxable !== 0 && service.taxable !== false,
        retailPrices,
        wholesalePrices,
        durations,
        expectedQuantities,
        currentOverlays,
        availableOverlays,
        trimOverrideCount,
        vehicleOverrides,
        productItemCount,
        workflowStepCount
      });
    }
  };

  // Load trim overrides for a service (returns count and list)
  loadTrimOverrides = async (serviceId) => {
    try {
      const { retailOverrides, wholesaleOverrides } =
        await ServicesAPI.getTrimOverridesForService(this.props.typeId, serviceId);

      // Combine overrides by trim, showing both retail and wholesale prices
      const overridesByTrim = {};

      retailOverrides.forEach(o => {
        const key = o.id_car_trim;
        if (!overridesByTrim[key]) {
          overridesByTrim[key] = {
            trimId: o.id_car_trim,
            make: o.make_name || 'Unknown',
            model: o.model_name || 'Unknown',
            trim: o.trim_name || 'Unknown',
            vehicleSize: o.vehicleSize
          };
        }
        overridesByTrim[key].retailPrice = o.override_price;
      });

      wholesaleOverrides.forEach(o => {
        const key = o.id_car_trim;
        if (!overridesByTrim[key]) {
          overridesByTrim[key] = {
            trimId: o.id_car_trim,
            make: o.make_name || 'Unknown',
            model: o.model_name || 'Unknown',
            trim: o.trim_name || 'Unknown',
            vehicleSize: o.vehicleSize
          };
        }
        overridesByTrim[key].wholesalePrice = o.override_price;
      });

      const vehicleOverrides = Object.values(overridesByTrim);
      return {
        count: vehicleOverrides.length,
        vehicles: vehicleOverrides
      };
    } catch (error) {
      console.error("Error loading trim overrides:", error);
      return { count: 0, vehicles: [] };
    }
  };

  // Load service item template counts (product items + workflow steps)
  loadItemTemplateCounts = async (serviceId) => {
    try {
      const templates = await getServiceItemTemplates(serviceId);
      // If server hasn't added the type column, items without type that have
      // product fields are material; items without type and no product fields are work steps.
      const materialCount = (templates || []).filter(t =>
        t.type !== 'work'
      ).length;
      const workCount = (templates || []).filter(t =>
        t.type === 'work'
      ).length;
      return { productItemCount: materialCount, workflowStepCount: workCount };
    } catch (error) {
      console.error("Error loading item template counts:", error);
      return { productItemCount: 0, workflowStepCount: 0 };
    }
  };

  // Refresh template counts after modal changes
  handleItemTemplatesChanged = async () => {
    if (this.state.selectedServiceId) {
      const { productItemCount, workflowStepCount } = await this.loadItemTemplateCounts(this.state.selectedServiceId);
      this.setState({ productItemCount, workflowStepCount });
    }
  };

  // Show vehicle pricing modal
  showVehiclePricing = () => {
    this.setState({ showVehiclePricingModal: true });
  };

  // Hide vehicle pricing modal
  hideVehiclePricing = () => {
    this.setState({ showVehiclePricingModal: false });
  };

  // Handle vehicle pricing update
  handleVehiclePricingUpdate = async () => {
    // Reload the trim overrides (count and list)
    if (this.state.selectedServiceId) {
      const { count: trimOverrideCount, vehicles: vehicleOverrides } =
        await this.loadTrimOverrides(this.state.selectedServiceId);
      this.setState({ trimOverrideCount, vehicleOverrides });
    }
  };

  // Add new service
  addService = async (serviceData) => {
    try {
      this.setState({ isLoading: true });

      // 1. Create master service definition
      const masterServiceResponse = await ServicesAPI.createMasterService({
        installerId: this.props.typeId,
        categoryId: this.state.selectedCategoryId,
        serviceName: serviceData.serviceName,
        coverageLevel: serviceData.coverageLevel || null,
        dataCollectRequired: serviceData.dataCollectRequired || "Y",
        notes: serviceData.notes || null,
        allowMultiple: serviceData.allowMultiple ? 1 : 0,
        taxable: serviceData.taxable ? 1 : 0
      });

      const newMasterServiceId = masterServiceResponse.id;

      // 2. Create all pricing records using bulk endpoint
      const priceMatrix = buildPriceMatrix(
        serviceData.retailPrices,
        serviceData.wholesalePrices,
        serviceData.durations,
        serviceData.expectedQuantities
      );

      await ServicesAPI.bulkCreatePricing(
        this.props.typeId,
        newMasterServiceId,
        priceMatrix
      );

      // 3. Add overlay relationships if any
      if (serviceData.overlays && serviceData.overlays.length > 0) {
        for (const overriddenId of serviceData.overlays) {
          await ServicesAPI.addServiceOverride(newMasterServiceId, overriddenId);
        }
      }

      alert("Service added successfully!");
      this.setState({ showModal: false, isLoading: false });

      // Refresh installer data (in case this is a new category)
      await this.loadInstallerData();
      // Reload services for current category and select the new service
      await this.loadServices(this.state.selectedCategoryId, true);
      await this.selectServiceByName(serviceData.serviceName);

    } catch (error) {
      console.error("Error adding service:", error);
      this.setState({ isLoading: false });
      alert("Error adding service: " + (error.response?.data?.message || error.message));
    }
  };

  // Update existing service
  updateService = async (serviceData) => {
    try {
      this.setState({ isLoading: true });
      const serviceId = this.state.selectedServiceId;

      // 1. Update master service definition
      await ServicesAPI.updateMasterService(serviceId, {
        serviceName: serviceData.serviceName,
        coverageLevel: serviceData.coverageLevel || null,
        dataCollectRequired: serviceData.dataCollectRequired || "Y",
        notes: serviceData.notes || null,
        allowMultiple: serviceData.allowMultiple ? 1 : 0,
        taxable: serviceData.taxable ? 1 : 0
      });

      // 2. Update pricing records
      const sizes = ['S', 'M', 'L', 'XL'];
      const catalogs = ['RETAIL', 'WHOLE'];

      for (const size of sizes) {
        for (const catalog of catalogs) {
          const price = catalog === 'RETAIL'
            ? serviceData.retailPrices[size]
            : serviceData.wholesalePrices[size];

          const durationDays = typeof serviceData.durations === 'number'
            ? serviceData.durations
            : serviceData.durations[size];

          await ServicesAPI.updatePriceByCompositeKey({
            installerId: this.props.typeId,
            masterServiceId: serviceId,
            vehicleSize: size,
            catalog: catalog,
            price: price || 0,
            durationDays: durationDays || 0,
            expectedQuantity: serviceData.expectedQuantities ? (serviceData.expectedQuantities[size] || 0) : 0
          });
        }
      }

      // 3. Update overlay relationships
      await this.updateOverlays(serviceId, serviceData.overlays || []);

      alert("Service updated successfully!");
      this.setState({ showModal: false, isLoading: false });

      // Refresh services and maintain selection
      await this.loadServices(this.state.selectedCategoryId, true);
      await this.selectServiceByName(serviceData.serviceName);

    } catch (error) {
      console.error("Error updating service:", error);
      this.setState({ isLoading: false });
      alert("Error updating service: " + (error.response?.data?.message || error.message));
    }
  };

  // Update overlay relationships (diff current vs new)
  updateOverlays = async (serviceId, newOverlayIds) => {
    // API returns { master_service_id, overridden_by_master_service_id, overridden_service_name }
    const currentOverlayIds = this.state.currentOverlays.map(o => o.master_service_id);

    // Find overlays to add
    const toAdd = newOverlayIds.filter(id => !currentOverlayIds.includes(id));

    // Find overlays to remove
    const toRemove = currentOverlayIds.filter(id => !newOverlayIds.includes(id));

    // Add new overlays
    for (const overriddenId of toAdd) {
      await ServicesAPI.addServiceOverride(serviceId, overriddenId);
    }

    // Remove old overlays
    for (const overriddenId of toRemove) {
      await ServicesAPI.removeServiceOverride(serviceId, overriddenId);
    }
  };

  // Delete service
  deleteService = async () => {
    const serviceId = this.state.selectedServiceId;
    const serviceName = this.state.selectedServiceName;

    if (!serviceId) {
      alert("No service selected for deletion");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${serviceName}"? This will remove all associated pricing. This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      this.setState({ isLoading: true });
      await ServicesAPI.deleteMasterService(serviceId);

      alert("Service deleted successfully!");

      // Reset selection
      this.setState({
        selectedServiceId: null,
        selectedServiceName: "Service",
        currentService: null,
        serviceName: "",
        coverageLevel: null,
        notes: "",
        dataCollectRequired: "Y",
        allowMultiple: false,
        taxable: true,
        retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
        wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
        durations: { XL: 0, L: 0, M: 0, S: 0 },
        currentOverlays: [],
        availableOverlays: [],
        isLoading: false
      });

      // Refresh installer data (in case last service in category was deleted)
      await this.loadInstallerData();
      // Reload services for current category
      if (this.state.selectedCategoryId) {
        await this.loadServices(this.state.selectedCategoryId, true);
      }

    } catch (error) {
      console.error("Error deleting service:", error);
      this.setState({ isLoading: false });

      if (error.response?.status === 400 && error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Error deleting service. Please try again.");
      }
    }
  };

  // Helper to select a service by name after refresh
  selectServiceByName = async (serviceName) => {
    const service = this.state.services.find(s => s.service_name === serviceName);
    if (service) {
      const { retailPrices, wholesalePrices, durations, expectedQuantities } = extractPriceMatrix(service.pricing || []);
      const currentOverlays = await this.loadOverlays(service.id);
      const availableOverlays = filterOverlayCandidates(this.state.services, service);

      this.setState({
        selectedServiceId: service.id,
        selectedServiceName: serviceName,
        currentService: service,
        serviceName: service.service_name,
        coverageLevel: service.coverage_level,
        notes: service.notes || "",
        dataCollectRequired: service.data_collect_required || "Y",
        allowMultiple: service.allow_multiple === 1 || service.allow_multiple === true,
        taxable: service.taxable !== 0 && service.taxable !== false,
        retailPrices,
        wholesalePrices,
        durations,
        expectedQuantities,
        currentOverlays,
        availableOverlays
      });
    }
  };

  // Get unit label for the currently selected category
  getSelectedCategoryUnitLabel = () => {
    const { selectedCategoryName, categoryConfigs, unitsById } = this.state;
    const config = categoryConfigs[selectedCategoryName];
    if (!config?.default_unit_id) return '';
    const unit = unitsById[config.default_unit_id];
    return unit?.unit_name || unit?.unit_code || '';
  };

  // Show add/update modal
  showService = (mode) => {
    this.setState({ showModal: true, modalAction: mode });
  };

  // Hide modal
  handleCancel = () => {
    this.setState({ showModal: false });
  };

  // Toggle between services and templates mode
  handleToggleMode = () => {
    this.setState(prevState => ({
      isTemplateMode: !prevState.isTemplateMode,
      selectedCategoryId: null,
      selectedCategoryName: "Category",
      selectedServiceId: null,
      selectedServiceName: "Service",
      services: [],
      currentService: null,
      showModal: false
    }));
  };

  render() {
    if (this.state.isTemplateMode) {
      return this.renderTemplateMode();
    }
    return this.renderServicesMode();
  }

  renderTemplateMode() {
    return (
      <React.Fragment>
        {/* Mode Toggle Buttons */}
        <div className="text-center mb-3">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn ${!this.state.isTemplateMode ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => this.state.isTemplateMode && this.handleToggleMode()}>
              SERVICES
            </button>
            <button
              type="button"
              className={`btn ${this.state.isTemplateMode ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => !this.state.isTemplateMode && this.handleToggleMode()}>
              TEMPLATES
            </button>
          </div>
        </div>

        <TemplateList typeId={this.props.typeId} />
      </React.Fragment>
    );
  }

  renderServicesMode() {
    // Role-based edit permission
    const userRole = this.props.user?.role;
    const canEdit = userRole === 'admin' || userRole === 'owner' || this.props.access === 'ADMIN';

    // Generate dropdown lists — show all global categories so installer can pick new ones
    const allCategoryNames = (this.state.allCategories || []).map(c => c.category_name);
    const categoryList = ["Category", ...allCategoryNames, ...(canEdit ? ["Add New Category"] : [])];
    const serviceList = ["Service", ...this.state.services.map(s => s.service_name)];

    // Table columns - show pricing and coverage
    const columns = [{
      dataField: 'vehicle_size',
      text: "Size",
      formatter: (cell) => {
        const sizeMap = { XL: 'X-Large', L: 'Large', M: 'Medium', S: 'Small' };
        return sizeMap[cell] || cell;
      }
    }, {
      dataField: 'retail_price',
      text: "Retail Price",
      formatter: (cell) => `$${(cell || 0).toFixed(2)}`
    }, {
      dataField: 'wholesale_price',
      text: "Wholesale Price",
      formatter: (cell) => cell ? `$${cell.toFixed(2)}` : 'N/A'
    }, {
      dataField: 'duration_days',
      text: "Duration",
      formatter: (cell) => `${cell || 0} days`
    }, {
      dataField: 'expected_quantity',
      text: "Expected Qty",
      formatter: (cell) => {
        if (!cell) return '-';
        const unitLabel = this.getSelectedCategoryUnitLabel();
        return unitLabel ? `${cell} ${unitLabel}` : `${cell}`;
      }
    }];

    // Prepare display data for selected service
    let displayData = [];
    if (this.state.selectedServiceId) {
      const vehicleSizes = ['XL', 'L', 'M', 'S'];
      displayData = vehicleSizes.map(size => ({
        vehicle_size: size,
        retail_price: this.state.retailPrices[size] || 0,
        wholesale_price: this.state.wholesalePrices[size] || 0,
        duration_days: this.state.durations[size] || 0,
        expected_quantity: this.state.expectedQuantities[size] || 0
      }));
    }

    // Coverage level display
    const coverageLabel = {
      'FRONT': 'Front Coverage',
      'REAR': 'Rear Coverage',
      'SIDE': 'Side Coverage',
      'ROOF': 'Roof Coverage',
      'FULL': 'Full Vehicle',
      'INTERIOR': 'Interior',
      'WHEEL': 'Wheel',
      null: 'N/A'
    };

    return (
      <React.Fragment>
        {/* Mode Toggle Buttons */}
        {canEdit && (
          <div className="text-center mb-3">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn ${!this.state.isTemplateMode ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => this.state.isTemplateMode && this.handleToggleMode()}>
                SERVICES
              </button>
              <button
                type="button"
                className={`btn ${this.state.isTemplateMode ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => !this.state.isTemplateMode && this.handleToggleMode()}>
                TEMPLATES
              </button>
            </div>
          </div>
        )}

        {/* Filter Form */}
        <form className="bg-complete shadow p-4 corners">
          <div className="form-row justify-content-center">
            <div className="form-group col-md-3">
              {!this.state.showAddCategory ? (
                <Select
                  name="categories"
                  label=" "
                  options={categoryList}
                  value={this.state.selectedCategoryName}
                  onChange={this.handleCategoryChange}
                />
              ) : (
                <div>
                  <label>&nbsp;</label>
                  <div className="d-flex align-items-center">
                    <input
                      type="text"
                      className="form-control"
                      value={this.state.newCategoryName}
                      onChange={this.handleNewCategoryChange}
                      placeholder="Enter new category name"
                      autoFocus
                    />
                    <Button
                      variant="success"
                      size="sm"
                      className="ml-2"
                      onClick={this.handleCreateCategory}
                      disabled={!this.state.newCategoryName.trim() || this.state.isLoading}
                    >
                      {this.state.isLoading ? '...' : 'Add'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="ml-1"
                      onClick={this.handleCancelNewCategory}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="form-group col-md-4">
              <Select
                name="services"
                label=" "
                options={serviceList}
                value={this.state.selectedServiceName}
                onChange={this.handleServiceChange}
                disabled={!this.state.selectedCategoryId}
              />
            </div>
          </div>
        </form>

        {/* Service Details */}
        {this.state.selectedServiceId && (
          <div className="card mt-3">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{this.state.serviceName}</h5>
                <div>
                  <span className={`badge ${this.state.coverageLevel ? 'badge-info' : 'badge-secondary'} mr-2`}>
                    {coverageLabel[this.state.coverageLevel]}
                  </span>
                  {this.state.allowMultiple && (
                    <span className="badge badge-success mr-2">
                      Multi-Qty
                    </span>
                  )}
                  {!this.state.taxable && (
                    <span className="badge badge-warning mr-2">
                      Non-Taxable
                    </span>
                  )}
                </div>
              </div>
              {this.state.notes && (
                <small className="text-muted">{this.state.notes}</small>
              )}
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <BootstrapTable
                  keyField='vehicle_size'
                  data={displayData}
                  columns={columns}
                  striped
                  hover
                  condensed
                />
              </div>

              {/* Vehicle-Specific Pricing Section */}
              <div className="border-top pt-3 mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-1">Vehicle-Specific Pricing</h6>
                    <small className="text-muted">
                      {this.state.trimOverrideCount > 0
                        ? `${this.state.trimOverrideCount} vehicle(s) have custom pricing for this service`
                        : 'No custom vehicle pricing set'
                      }
                    </small>
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={this.showVehiclePricing}
                    >
                      {this.state.trimOverrideCount > 0 ? 'Manage' : 'Add Custom Pricing'}
                    </button>
                  )}
                </div>

                {/* List of vehicles with custom pricing */}
                {this.state.vehicleOverrides.length > 0 && (
                  <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table className="table table-sm table-striped mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>Vehicle</th>
                          <th className="text-right">Retail</th>
                          <th className="text-right">Wholesale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.state.vehicleOverrides.map((v, idx) => (
                          <tr key={idx}>
                            <td>
                              <strong>{v.make}</strong> {v.model}
                              {v.trim && v.trim !== 'Unknown' && (
                                <small className="text-muted ml-1">({v.trim})</small>
                              )}
                            </td>
                            <td className="text-right">
                              {v.retailPrice ? `$${v.retailPrice.toFixed(2)}` : '-'}
                            </td>
                            <td className="text-right">
                              {v.wholesalePrice ? `$${v.wholesalePrice.toFixed(2)}` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Preferred Products Section */}
              <div className="border-top pt-3 mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-1">
                      <i className="fa-sharp fa-regular fa-cube mr-1 text-muted"></i>
                      Preferred Product(s)
                    </h6>
                    <small className="text-muted">
                      {this.state.productItemCount > 0
                        ? `${this.state.productItemCount} preferred product${this.state.productItemCount !== 1 ? 's' : ''} defined`
                        : 'No preferred products defined'
                      }
                    </small>
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => this.setState({ showProductItemTemplateModal: true })}
                    >
                      {this.state.productItemCount > 0 ? 'Manage Products' : 'Add Products'}
                    </button>
                  )}
                </div>
              </div>

              {/* Workflow Steps Section */}
              <div className="border-top pt-3 mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-1">
                      <i className="fa-sharp fa-regular fa-tasks mr-1 text-muted"></i>
                      Workflow Steps
                    </h6>
                    <small className="text-muted">
                      {this.state.workflowStepCount > 0
                        ? `${this.state.workflowStepCount} workflow step${this.state.workflowStepCount !== 1 ? 's' : ''} defined`
                        : 'No workflow steps defined'
                      }
                    </small>
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => this.setState({ showWorkflowStepTemplateModal: true })}
                    >
                      {this.state.workflowStepCount > 0 ? 'Manage Steps' : 'Add Steps'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {canEdit && (
          <div className="col text-center mt-3">
            <button
              type="button"
              className="btn shadow btn-success m-2 buttons"
              onClick={() => this.showService("add")}
              disabled={!this.state.selectedCategoryId || this.state.isLoading}
            >
              {this.state.isLoading ? 'Loading...' : 'ADD NEW SERVICE'}
            </button>
            {this.state.selectedServiceId && (
              <>
                <button
                  type="button"
                  className="btn shadow btn-primary m-2 buttons"
                  onClick={() => this.showService("update")}
                  disabled={this.state.isLoading}
                >
                  UPDATE SERVICE
                </button>
                <button
                  type="button"
                  className="btn shadow btn-danger m-2 buttons"
                  onClick={this.deleteService}
                  disabled={this.state.isLoading}
                >
                  DELETE SERVICE
                </button>
              </>
            )}
          </div>
        )}

        {/* Service Modal */}
        <AddService
          show={this.state.showModal}
          onHide={this.handleCancel}
          installer={this.props.typeId}
          access={this.props.access}
          action={this.state.modalAction}
          // Service data
          categoryId={this.state.selectedCategoryId}
          categoryName={this.state.selectedCategoryName}
          serviceName={this.state.modalAction === "update" ? this.state.serviceName : ""}
          coverageLevel={this.state.modalAction === "update" ? this.state.coverageLevel : null}
          notes={this.state.modalAction === "update" ? this.state.notes : ""}
          dataCollectRequired={this.state.modalAction === "update" ? this.state.dataCollectRequired : "Y"}
          allowMultiple={this.state.modalAction === "update" ? this.state.allowMultiple : false}
          taxable={this.state.modalAction === "update" ? this.state.taxable : true}
          retailPrices={this.state.modalAction === "update" ? this.state.retailPrices : { XL: 0, L: 0, M: 0, S: 0 }}
          wholesalePrices={this.state.modalAction === "update" ? this.state.wholesalePrices : { XL: 0, L: 0, M: 0, S: 0 }}
          durations={this.state.modalAction === "update" ? this.state.durations : { XL: 0, L: 0, M: 0, S: 0 }}
          expectedQuantities={this.state.modalAction === "update" ? this.state.expectedQuantities : { XL: 0, L: 0, M: 0, S: 0 }}
          unitLabel={this.getSelectedCategoryUnitLabel()}
          // Overlays
          currentOverlays={this.state.modalAction === "update" ? this.state.currentOverlays : []}
          availableOverlays={this.state.availableOverlays}
          // Callbacks
          onAdd={this.addService}
          onUpdate={this.updateService}
        />

        {/* Vehicle-Specific Pricing Modal */}
        <VehicleSpecificPricing
          show={this.state.showVehiclePricingModal}
          onHide={this.hideVehiclePricing}
          installerId={this.props.typeId}
          preSelectedService={this.state.selectedServiceId ? {
            id: this.state.selectedServiceId,
            service_name: this.state.serviceName
          } : null}
          onUpdate={this.handleVehiclePricingUpdate}
        />

        {/* Product Item Template Modal */}
        {this.state.showProductItemTemplateModal && (
          <ProductItemTemplateModal
            show={this.state.showProductItemTemplateModal}
            onHide={() => this.setState({ showProductItemTemplateModal: false })}
            masterServiceId={this.state.selectedServiceId}
            installerId={this.props.typeId}
            categoryName={this.state.selectedCategoryName}
            serviceName={this.state.serviceName}
            onTemplatesChanged={this.handleItemTemplatesChanged}
          />
        )}

        {/* Workflow Step Template Modal */}
        {this.state.showWorkflowStepTemplateModal && (
          <WorkflowStepTemplateModal
            show={this.state.showWorkflowStepTemplateModal}
            onHide={() => this.setState({ showWorkflowStepTemplateModal: false })}
            masterServiceId={this.state.selectedServiceId}
            onTemplatesChanged={this.handleItemTemplatesChanged}
          />
        )}
      </React.Fragment>
    );
  }
}
